"use client";

import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import { postReview } from "@/lib/actions/social";
import { SELF_SERVE_REVIEW_TAGS } from "@/lib/reviewTags";

const TAGS = SELF_SERVE_REVIEW_TAGS;

export function ReviewForm({
  bookId,
  slug,
  signedIn,
}: {
  bookId: string;
  slug: string;
  signedIn: boolean;
}) {
  const router = useRouter();
  const [text, setText] = useState("");
  const [tag, setTag] = useState<string>(TAGS[0]);
  const [pending, start] = useTransition();
  const [done, setDone] = useState(false);
  const [error, setError] = useState(false);

  if (!signedIn) {
    return (
      <div className="rounded-[14px] border border-dashed border-border bg-surface/50 p-5 text-[14px] text-muted">
        <button
          onClick={() => router.push("/signin?next=" + encodeURIComponent(`/books/${slug}`))}
          className="font-semibold text-copper"
        >
          Sign in
        </button>{" "}
        to leave a review.
      </div>
    );
  }

  if (done) {
    return (
      <div className="rounded-[14px] border border-border bg-badge-green-bg/50 p-5 text-[14px] text-green">
        Thanks — your review is posted.
      </div>
    );
  }

  return (
    <div className="rounded-[14px] border border-border bg-surface p-5">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder="Share what stood out about this rule book…"
        className="w-full resize-none rounded-[10px] border border-border bg-paper px-3 py-2.5 text-[15px] text-ink outline-none focus:outline-2 focus:outline-copper"
      />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {TAGS.map((t) => (
          <button
            key={t}
            onClick={() => setTag(t)}
            className="rounded-full px-3 py-1 text-[12px] font-semibold"
            style={
              tag === t
                ? { background: "var(--copper)", color: "var(--surface)" }
                : { background: "var(--surface-alt2)", color: "var(--muted)" }
            }
          >
            {t}
          </button>
        ))}
        <button
          disabled={pending || !text.trim()}
          onClick={() =>
            start(async () => {
              setError(false);
              try {
                const res = await postReview(bookId, slug, text, tag);
                if (res.ok) {
                  setDone(true);
                  router.refresh();
                } else {
                  setError(true);
                }
              } catch {
                setError(true);
              }
            })
          }
          className="ml-auto h-[40px] rounded-[10px] bg-green px-5 text-[13px] font-bold text-surface disabled:opacity-50"
        >
          {pending ? "Posting…" : "Post review"}
        </button>
      </div>
      {error && (
        <p className="mt-2 text-[12.5px] font-semibold text-danger" role="alert">
          That didn&apos;t post — try again.
        </p>
      )}
    </div>
  );
}
